import { RecommendationModel } from 'projects/core/src/lib/models/recommendation.model';
import { BaseModel } from 'projects/core/src/lib/models/base.model';
import { ResourceModel } from 'projects/core/src/lib/models/resource.model';
import { BehaviorSubject } from 'rxjs';
import { RatingtypeModel } from './ratingtype.model';
import * as moment from 'moment-timezone';
import { CandidateModel } from './candidate.model';
import { SlotModel, SlotType } from './slot.model';
import { PreparationModel } from './preparation.model';
import { RunnerOnlinetestModel } from './runner-onlinetest.model';
import { STATUS_NAMES, OBSERVER_OPENABLE_TIMEOUT } from 'projects/core/src/lib/constants';
import { AcStatusSettingsExerciseCandidateType, AcStatusSettingsExerciseGroupType, AcStatusSettingsExerciseObserverType, AcStatusSettingsExerciseType, AcStatusType, AcTimingStatus, ActiveTiming, RatingstartType, SlotStatus, TimerType, BusyType } from './acstatus/ac-status.type';
import { ObserverModel } from './observer.model';
import { CandidateAcOnlinetestModel } from 'projects/core/src/lib/models/candidate-ac-onlinetest.model';
import { ExerciseModel } from './exercise.model';
import { SINGLE_GROUP_UUID, TIMING_DIV } from './constants';
import * as CryptoJS from 'crypto-js';
import { ExerciseContainer, ExerciseContainerType } from '../partials/agenda/agenda/agenda.component';
import { UserModel } from './user.model';
import { CompetenceModel } from 'projects/core/src/lib/models/competence.model';

export enum AcType {
  Single = 'single',
  Group = 'group',
}

export class AcModel extends BaseModel {

  // columns
  name: string;
  status: string;
  language: string;
  location_name: string; // tslint:disable-line: variable-name
  start_time: any; // tslint:disable-line: variable-name
  preparation_time: any; // tslint:disable-line: variable-name
  started_at: any; // tslint:disable-line: variable-name
  informations_moderator: string; // tslint:disable-line: variable-name
  documents_visible_observer: boolean; // tslint:disable-line: variable-name
  country: string;
  timezone: string;
  tz_offset: string; // tslint:disable-line: variable-name
  type: AcType;

  // relations
  moderator: UserModel = null;
  candidates: Array<CandidateModel> = [];
  observers: Array<ObserverModel> = [];
  ratingtype: RatingtypeModel = null;
  files: Array<ResourceModel> = [];
  slots: Array<SlotModel> = [];
  exercises: Array<ExerciseModel> = [];
  preparations: Array<PreparationModel> = [];
  recommendations: Array<RecommendationModel> = [];
  onlinetests: Array<RunnerOnlinetestModel> = [];
  competences: Array<CompetenceModel> = [];
  virtual: boolean;
  conference_url: string; // tslint:disable-line: variable-name
  conference_info: string; // tslint:disable-line: variable-name

  // status
  ac_status: AcStatusType = null; // tslint:disable-line: variable-name

  // client attributes
  startedAt: any = null;
  finishedAt: any = null;

  // misc
  notices: { [key: string]: string } | null = null;
  ratings: Array<any> = null;
  containers: Array<ExerciseContainer> = [];
  authType: string = null;

  // status subject
  private acStatusSubject: BehaviorSubject<AcStatusType> = new BehaviorSubject(null);
  public acStatus$ = this.acStatusSubject.asObservable();

  // info for reloading
  private reload = false;
  private reloadSubject: BehaviorSubject<boolean> = new BehaviorSubject(this.reload);
  public reload$ = this.reloadSubject.asObservable();

  // hashes for ac status
  private statusHashs = {};

  deserialize(input: any, mapStatus = true): this {
    Object.assign(this, input);
    // ratingtype
    if (this.ratingtype !== null && this.ratingtype !== undefined) {
      this.ratingtype = new RatingtypeModel().deserialize(this.ratingtype);
    }
    // moderator
    if (this.moderator !== null && this.moderator !== undefined) {
      this.moderator = new UserModel().deserialize(this.moderator);
    }
    // candidates
    let candidates = null;
    if (this.candidates !== null && this.candidates !== undefined) {
      candidates = [];
      this.candidates.forEach((candidate: CandidateModel) => {
        candidates.push(new CandidateModel().deserialize(candidate));
      });
    }
    this.candidates = candidates;
    // observers
    let observers = null;
    if (this.observers !== null && this.observers !== undefined) {
      observers = [];
      this.observers.forEach((observer: ObserverModel) => {
        observers.push(new ObserverModel().deserialize(observer));
      });
    }
    this.observers = observers;
    // add moderator as observer
    this.observers.push(new ObserverModel().deserialize(this.moderator));
    // preparations
    let preparations = null;
    if (this.preparations !== null && this.preparations !== undefined) {
      preparations = [];
      this.preparations.forEach((preparation: PreparationModel) => {
        preparations.push(new PreparationModel().deserialize(preparation));
      });
    }
    this.preparations = preparations.sort((a: any, b: any) => (a.visible_at > b.visible_at) ? 1 : ((b.visible_at > a.visible_at) ? -1 : 0));
    // files
    let files = null;
    if (this.files !== null && this.files !== undefined) {
      files = [];
      this.files.forEach((file: ResourceModel) => {
        files.push(new ResourceModel().deserialize(file));
      });
    }
    this.files = files;
    // recommendations
    let recommendations = null;
    if (this.recommendations !== null && this.recommendations !== undefined) {
      recommendations = [];
      this.recommendations.forEach((recommendation: RecommendationModel) => {
        recommendations.push(new RecommendationModel().deserialize(recommendation));
      });
    }
    this.recommendations = recommendations;
    // onlinetests
    let onlinetests = null;
    if (this.onlinetests !== null && this.onlinetests !== undefined) {
      onlinetests = [];
      this.onlinetests.forEach((onlinetest: RunnerOnlinetestModel) => {
        onlinetests.push(new RunnerOnlinetestModel().deserialize(onlinetest));
      });
    }
    this.onlinetests = onlinetests;
    // exercises
    let exercises = null;
    if (this.exercises !== null && this.exercises !== undefined) {
      exercises = [];
      this.exercises.forEach((exercise: ExerciseModel) => {
        const newExercise = new ExerciseModel().deserialize(exercise);
        newExercise.ac = this;
        newExercise.slots = [];
        exercises.push(newExercise);
      });
    }
    this.exercises = exercises;
    // slots
    let slots = null;
    if (this.slots !== null && this.slots !== undefined) {
      slots = [];
      this.slots.sort((a: SlotModel, b: SlotModel) => {
        return (a.start_time > b.start_time) ? 1 : ((b.start_time > a.start_time) ? -1 : 0);
      }).forEach((slot: SlotModel) => {
        const newSlot = new SlotModel().deserialize(slot);
        const exercise = this.exercises.find(exercise => exercise.id === slot.exercise_id) || null;
        if (exercise) {
          exercise.slots.push(newSlot);
        }
        newSlot.exercise = exercise;
        newSlot.ac = this;
        slots.push(newSlot);
      });
    }
    this.slots = slots;
    // competences
    this.competences = this.competences?.map((competence: any) => {
      return new CompetenceModel().deserialize(competence);
    }) || [];
    // ADJUST TIMEZONE
    this.start_time = moment.tz(this.start_time, this.timezone);
    // map ac status
    if (mapStatus === true) {
      this.mapAcStatus();
    }
    this.loaded = true;
    return this;
  }

  public getStatusName(): string {
    return STATUS_NAMES[this.status];
  }

  // ac control

  public isFinishable(): boolean {
    return this.isRunning() && this.candidates.filter((candidate: CandidateModel) => {
      return candidate.recommendation === null;
    }).length === 0;
  }

  public isFinished(): boolean {
    return this.status === 'finished';
  }

  public isRunning(): boolean {
    return this.status === 'running';
  }

  // child data

  public getExercise(id: number): ExerciseModel | null {
    return this.exercises.find(exercise => exercise.id === id) || null;
  }

  // exercise control

  public getExerciseStatus(id: number): AcStatusSettingsExerciseType {
    return this.ac_status?.settings?.exercises[id] || this.getSingleAcPseudoExerciseStatus();
  }

  public getSingleAcPseudoExerciseStatus(): AcStatusSettingsExerciseType {
    const groups: { [key: string]: AcStatusSettingsExerciseGroupType } = {};
    const candidates: { [key: string]: AcStatusSettingsExerciseCandidateType | null } = {};
    const observers: { [key: string]: AcStatusSettingsExerciseObserverType | null } = {};
    groups[SINGLE_GROUP_UUID] = {
      name: null,
      moderator: null
    };
    this.candidates?.forEach((candidate: CandidateModel) => {
      candidates[candidate.hashed_id] = {
        group: SINGLE_GROUP_UUID
      };
    });
    this.observers?.forEach((observer: ObserverModel) => {
      observers[observer.hashed_id] = {
        group: SINGLE_GROUP_UUID
      };
    });
    const status: AcStatusSettingsExerciseType = {
      candidates,
      observers,
      groups
    };
    return status;
  }


  // misc

  public getOnlinetestOnId(id: number): RunnerOnlinetestModel | null {
    return this.onlinetests.find((onlinetest: RunnerOnlinetestModel) => id === onlinetest.id) || null;
  }

  public getSlotOnId(id: number): SlotModel | null {
    return this.slots.find((actual: SlotModel) => actual.id === id) || null;
  }

  public getConference(): SlotModel | null {
    return this.slots.find((actual: SlotModel) => actual.type === 'conference') || null;
  }

  public getPreparationOnId(id: number): PreparationModel | null {
    return this.preparations.find((actual: PreparationModel) => actual.id === id) || null;
  }

  public isStartable(): boolean {
    return this.status === 'planned' && moment().isSameOrAfter(this.start_time, 'day');
  }

  public getAcDay(): any {
    return moment(this.start_time).startOf('day');
  }

  public isToday(): boolean {
    return moment(this.start_time).isSame(moment(), 'day');
  }

  public getVisibleMode(): string {
    const startOfAcDay = this.getAcDay();
    if (moment().isAfter(startOfAcDay)) {
      return 'ac';
    } else  {
      return 'preparation';
    }
  }

  public getPreparationTime(): any | null {
    return (moment(this.preparation_time).isBefore(this.getAcDay())) ? moment(this.preparation_time) : null;
  }

  public hasFiles(userType: string): boolean {
    if (this.files !== null && this.files.length > 0) {
      return this.files.filter((file: ResourceModel) => {
        return file.hasRight(userType) === true;
      }).length > 0;
    }
    return false;
  }

  public getDateFormat(short: boolean): string {
    if (this.language === 'DE') {
      return (short === true) ? 'HH:mm' : 'DD.MM.YYYY HH:mm';
    } else {
      return (short === true) ? 'HH:mm a' : 'DD.MM.YYYY HH:mm a';
    }
  }

  public isWithinObserverOpenableTime(): boolean {
    return this.documents_visible_observer === true ||
      moment().isAfter(moment(this.start_time).subtract(OBSERVER_OPENABLE_TIMEOUT, 'minutes'));
  }

  public isGroup(): boolean {
    return this.type === AcType.Group;
  }

  public isSingle(): boolean {
    return this.type === AcType.Single;
  }

  // ac status management

  public getCandidate(hashed_id: string): CandidateModel | null {
    return this.candidates.find((candidate: CandidateModel) => candidate.hashed_id === hashed_id) || null;
  }

  public getUser(hashed_id: string): CandidateModel | ObserverModel | null {
    let user = null;
    user = this.candidates.find((candidate: CandidateModel) => candidate.hashed_id === hashed_id) || null;
    if (user !== null) {
      return user;
    }
    user = this.observers.find((observer: ObserverModel) => observer.hashed_id === hashed_id) || null;
    if (user !== null) {
      return user;
    }
    return user;
  }

  /**
   * maps the status informations for the ac to the relevant child models
   *
   * @returns void
   */
  public mapAcStatus(force = false): boolean {
    let hasChangedData = false;
    if (this.statusHashWasChanged('base') || force === true) {
      // main status
      this.status = this.ac_status?.base?.status || AcTimingStatus.Planned;
      this.startedAt = this.ac_status?.base?.startedAt || null;
      this.finishedAt = this.ac_status?.base?.finishedAt || null;
    }
    if (this.statusHashWasChanged('users') || force === true) {
      // set users status
      this.candidates.forEach((candidate: CandidateModel) => {
        if (this.ac_status?.users[candidate.hashed_id]) {
          candidate.setUserStatus(this.ac_status.users[candidate.hashed_id]);
        }
      });
      this.observers.forEach((observer: ObserverModel) => {
        if (this.ac_status?.users[observer.hashed_id]) {
          observer.setUserStatus(this.ac_status.users[observer.hashed_id]);
        }
      });
      if (this.ac_status?.users[this.moderator.hashed_id]) {
        this.moderator.setUserStatus(this.ac_status.users[this.moderator.hashed_id]);
      }
    }
    let setBusy = false;
    let settingsChanged = false;
    let timingChanged = false;
    if (this.statusHashWasChanged('settings') || force === true) {
      // set exercise settings
      setBusy = true;
      settingsChanged = true;
      hasChangedData = true;
      this.exercises.forEach((exercise: ExerciseModel) => {
        exercise.setStatusSettings(this.ac_status?.settings?.exercises[exercise.id] || this.getSingleAcPseudoExerciseStatus());
      });
    }
    const slotsAsObject: { [key: number]: SlotModel } = {};
    for (const slot of this.slots) {
      slotsAsObject[slot['id']] = slot;
    }
    if (this.statusHashWasChanged('timing') || force === true) {
      setBusy = true;
      timingChanged = true;
      // set timing for slots (pre candidate and/or group)
      for (const slot of this.slots) {
        slot.resetTimings();
      }
      if (this.ac_status?.timing) {
        Object.keys(this.ac_status?.timing).forEach((key: string) => {
          const parts = key.split(TIMING_DIV);
          if (parts[0] && parts[1]) {
            const slotId = parseInt(parts[0], 10);
            const hash = parts[1];
            const slot = slotsAsObject[slotId];
            if ((slot instanceof SlotModel) && hash && this.ac_status?.timing[key]) {
              slot.setTiming(hash, this.ac_status?.timing[key]);
            }
          }
        });
      }
    }
    // set busy flag for all users
    if (setBusy === true || force === true) {
      if (this.ac_status?.timing) {
        // reset candidates, obervers
        this.candidates.forEach((candidate: CandidateModel) => { candidate.busy = null; });
        this.observers.forEach((observer: ObserverModel) => { observer.busy = null; });
        // load timings
        const activeTimings: Array<ActiveTiming> = [];
        Object.keys(this.ac_status?.timing).forEach((key: string) => {
          const timing = this.ac_status?.timing[key];
          if (
            timing?.exercise?.status === SlotStatus.Running ||
            timing?.exercise?.status === SlotStatus.Paused ||
            timing?.rating?.status === SlotStatus.Running ||
            timing?.rating?.status === SlotStatus.Paused
          ) {
            const parts = key.split(TIMING_DIV);
            const slot = slotsAsObject[parseInt(parts[0], 10)] || null;
            if (slot !== null) {
              activeTimings.push({
                hash: parts[1],
                slot,
                timing
              });
            }
          }
        });
        this.exercises.forEach((exercise: ExerciseModel) => {
          exercise.setBusyFlags(activeTimings);
        });
        // set busy for conference
        const conferenceTiming = activeTimings.find(timing => timing.hash === 'conference') || null;
        if (conferenceTiming !== null) {
          this.observers.forEach((observer: ObserverModel) => {
            observer.busy = {
              slot: conferenceTiming.slot
            };
          });
        }
      }
    }
    if (
      this.containers.length === 0 ||
      settingsChanged === true ||
      timingChanged === true ||
      force === true
    ) {
      // set containers
      this.initContainers();
    }
    return hasChangedData;
  }

  private statusHashWasChanged(type: string): boolean {
    const actual = this.getStatusHash(type);
    const changed = this.statusHashs[type] === undefined || this.statusHashs[type] !== actual;
    this.statusHashs[type] = actual;
    return changed;
  }

  private getStatusHash(type: string): string | null {
    if (this.ac_status && this.ac_status[type]) {
      const jsonString = JSON.stringify(this.ac_status[type]);
      const hash = CryptoJS.SHA256(jsonString);
      return hash.toString(CryptoJS.enc.Hex);
    }
    return null;
  }

  // online tests

  setNewOnlinetestReports(data: Array<any>): number {
    let oldFileCount = 0;
    let newFileCount = 0;
    // reset old resources
    this.onlinetests?.forEach((onlinetest: RunnerOnlinetestModel) => {
      onlinetest.candidates?.forEach((candidate: CandidateAcOnlinetestModel) => {
        oldFileCount += candidate.resources?.length || 0;
        candidate.resources = [];
      });
    });
    // add resources
    data.forEach((entry: any) => {
      const onlinetest = this.onlinetests?.find(actual => actual.id === entry.ac_onlinetest_id);
      if (onlinetest) {
        const candidate = onlinetest.candidates?.find(actual => actual.hashed_id === entry.candidate_id);
        if (candidate) {
          const resource = candidate.resources?.find(actual => actual.id === entry.report.id);
          if (resource === undefined) {
            newFileCount++;
            candidate.resources.push(new ResourceModel().deserialize(entry.report));
          }
        }
      }
    });
    return Math.max(0, newFileCount - oldFileCount);
  }

  // timezones

  getTimeZoneFullname(): string {
    return this.timezone + ' GMT' + this.tz_offset;
  }

  // containers

  /**
   * Inits the containers on agenda
   */
  public initContainers(): void {
    // sort slots
    this.slots.sort((a: SlotModel, b: SlotModel) => {
      if (a.start_time === b.start_time && a.type === SlotType.Exercisepart && b.type !== SlotType.Exercisepart) {
        return 1;
      } else if (a.start_time === b.start_time && a.type !== SlotType.Exercisepart && b.type === SlotType.Exercisepart) {
        return -1;
      } else {
        return (a.start_time > b.start_time) ? 1 : ((b.start_time > a.start_time) ? -1 : 0);
      }
    });
    // adjust start times with duration from settings
    let filteredSlots = this.slots;
    let gap = 0;
    this.slots.forEach((slot: SlotModel) => {
      slot.calculatedStartTime = slot.start_time + gap;
      gap += (slot.duration - slot.baseDuration);
      if (slot.type === SlotType.Exercisepart && slot.rating_start === RatingstartType.End) {
        gap += (slot.rating_duration - slot.baseRatingDuration);
      }
    });
    // filter after user type
    if (this.isSingle()) {
      if (this.authType === 'candidate') {
        filteredSlots = filteredSlots.filter((slot: SlotModel) => {
          return slot.for_candidate === true;
        });
      } else if (this.authType === 'observer') {
        filteredSlots = filteredSlots.filter((slot: SlotModel) => {
          return slot.for_observer === true;
        });
      }
    } else if (this.isGroup() && this.authType === 'candidate') {
      filteredSlots.forEach((slot: SlotModel) => {
        if (slot.type === SlotType.Conference) {
          slot.type = SlotType.Break;
        }
      });
    }
    // put slots into containers
    let slotQueue: Array<SlotModel> = [];
    this.containers = [];
    filteredSlots.forEach((slot: SlotModel, index: number) => {
      const nextSlot: SlotModel = filteredSlots[index + 1] || null;
      const type = slot.type === SlotType.Exercisepart ? ExerciseContainerType.Exercise : (slot.type.toString() as ExerciseContainerType);
      const name = slot.type === SlotType.Exercisepart ? slot.exercisename : slot.name;
      slotQueue.push(slot);
      if (
        slot.type !== SlotType.Exercisepart ||
        nextSlot?.type !== SlotType.Exercisepart ||
        slot.exercise_id !== nextSlot?.exercise_id ||
        nextSlot === null
      ) {
        if (slotQueue.length > 0) {
          const container = {
            id: slotQueue[0].id,
            type,
            name,
            slots: slotQueue,
            exercise: (slotQueue[0].exercise) ? slotQueue[0].exercise : null,
            time: moment(this.start_time).add(slotQueue[0].calculatedStartTime, 'minutes')
          };
          slotQueue.forEach((slot: SlotModel) => {
            slot.container = container;
          });
          this.containers.push(container);
        }
        slotQueue = [];
      }
    });
    // set container part numbers
    this.setContainerParts();
    // set container durations
    this.setContainerDurations();
    // adjust container start times: group ac only
    if (this.isGroup()) {
      this.adjustContainerStarttimes();
    }
  }

  /**
   * Checks if the amount of participants has changed
   *
   * @param candidates number
   * @param observers number
   * @returns boolean
   */
  public hasParticipantsChanged(candidates: number = 0, observers: number = 0): boolean {
    const acObservers = this.observers.filter((observer: ObserverModel) => {
      return observer.role !== 'moderator';
    });
    return this.candidates.length !== candidates || acObservers.length !== observers;
  }

  /**
   * Adds text (part 1/2/3 etc.) to container names if necessary
   */
  private setContainerParts(): void {
    this.exercises.forEach((exercise: ExerciseModel) => {
      const containers = this.containers.filter((container: ExerciseContainer) => {
        return container.type === ExerciseContainerType.Exercise && container.exercise?.id === exercise.id;
      });
      if (containers.length > 1) {
        containers.forEach((container: ExerciseContainer, index: number) => {
          container.part = index + 1;
        });
      }
    });
  }

  /**
   * Sets or adjusts the duration of all containers
   */
  private setContainerDurations(): void {
    this.containers.forEach((container: ExerciseContainer) => {
      if (container.type === ExerciseContainerType.Exercise) {
        if (this.isSingle()) {
          container.duration = container.exercise?.getSlotlistDuration(container.slots, this.authType) || 0;
        } else {
          container.duration = container.exercise?.getGroupDuration() || 0;
        }
      } else {
        container.duration = (container.slots?.length > 0) ? container.slots[0].duration : 0;
      }
    });
  }

  /**
   * Sets the start times for containers
   */
  private adjustContainerStarttimes(): void {
    let duration = 0;
    this.containers.forEach((container: ExerciseContainer) => {
      const lastTime = container.time;
      const time = moment(this.start_time).add(duration, 'minutes');
      const gap = time.diff(lastTime, 'minutes');
      container.time = time;
      if (container.slots.length > 0) {
        container.slots.forEach((slot: SlotModel) => {
          slot.calculatedStartTime += gap;
        });
      }
      duration += container.duration;
    });
  }


  // control reloads

  /**
   * Sets the reload flag
   */
  public setReload(): void {
    this.reload = true;
    this.reloadSubject.next(true);
  }

  /**
   * Unsets the reload flag
   */
  public unsetReload(): void {
    this.reload = false;
    this.reloadSubject.next(false);
  }

  /**
   * Gets the reload flag
   */
  public getReload(): boolean {
    return this.reload;
  }

}
